import { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import {
  Loader2, Trash2, Copy, Check, ChevronDown, ChevronUp,
  Type, Clock, Sparkles, Upload, X, Film, Image as ImageIcon, Eye,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import api from '../lib/api';
import { chunkedUpload } from '../lib/chunkedUpload';

const POST_TYPES = [
  { value: 'reel', label: 'Reel' },
  { value: 'static', label: 'Static Post' },
  { value: 'carousel', label: 'Carousel' },
];

const PLATFORMS = [
  { value: 'both', label: 'IG + TikTok' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'tiktok', label: 'TikTok' },
];

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const CaptionGenerator = () => {
  const [inputText, setInputText] = useState('');
  const [postType, setPostType] = useState('reel');
  const [platform, setPlatform] = useState('both');
  const [generating, setGenerating] = useState(false);
  const [progressMsg, setProgressMsg] = useState('');
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [expandedHistory, setExpandedHistory] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [showAnalysis, setShowAnalysis] = useState(false);

  // Media state
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [mediaKind, setMediaKind] = useState(null); // 'video' | 'image'
  const fileRef = useRef(null);

  useEffect(() => { fetchHistory(); }, []);

  const fetchHistory = async () => {
    try {
      const res = await api.get('/caption-generator/history');
      setHistory(res.data || []);
    } catch { /* silent */ }
  };

  const pickFile = (f) => {
    if (!f) return;
    const isVideo = f.type.startsWith('video/');
    const isImage = f.type.startsWith('image/');
    if (!isVideo && !isImage) { toast.error('Upload a video or an image'); return; }
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(f);
    setMediaKind(isVideo ? 'video' : 'image');
    setPreviewUrl(URL.createObjectURL(f));
  };

  const clearMedia = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(null);
    setPreviewUrl(null);
    setMediaKind(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleGenerate = async () => {
    if (!inputText.trim() && !file) {
      toast.error('Add a note, or upload a video/image (or both)');
      return;
    }
    setGenerating(true);
    setResult(null);
    setShowAnalysis(false);
    try {
      let mediaInfo = null;
      if (file) {
        setProgressMsg(`Uploading ${mediaKind}…`);
        const up = await chunkedUpload(file, 'caption_media', 'caption', (p) => {
          setProgressMsg(`Uploading ${mediaKind}… ${p}%`);
        });
        mediaInfo = {
          media_r2_key: up.r2_key,
          media_content_type: up.content_type,
          media_name: up.original_name,
        };
      }

      setProgressMsg(file
        ? (mediaKind === 'video' ? 'Gemini is watching your video…' : 'Gemini is viewing your image…')
        : 'Crafting your captions…');

      const res = await api.post('/caption-generator/generate', {
        input_text: inputText.trim(),
        post_type: postType,
        platform,
        ...(mediaInfo || {}),
      }, { timeout: 120000 });

      let record = res.data;

      // Media path returns status=processing → poll until ready.
      if (record.status === 'processing') {
        for (let i = 0; i < 120; i++) { // up to ~6 min
          await sleep(3000);
          try {
            const poll = await api.get(`/caption-generator/${record.id}`);
            record = poll.data;
            if (record.status === 'ready' || record.status === 'error') break;
          } catch { /* keep polling */ }
        }
      }

      if (record.status === 'error') {
        toast.error(record.error || 'Generation failed');
      } else if (record.status !== 'ready') {
        toast.error('Still processing — check back in a moment (see history)');
      } else {
        setResult(record);
        toast.success('Captions generated');
      }
      clearMedia();
      fetchHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Generation failed');
    } finally {
      setGenerating(false);
      setProgressMsg('');
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/caption-generator/${id}`);
      setHistory((prev) => prev.filter((h) => h.id !== id));
      if (result?.id === id) setResult(null);
      toast.success('Deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const copyCaption = async (text, id) => {
    const done = () => {
      setCopiedId(id);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopiedId(null), 2000);
    };
    try {
      await navigator.clipboard.writeText(text);
      done();
    } catch {
      // navigator.clipboard needs a secure context + focus; fall back for
      // iframe/preview contexts where it throws NotAllowedError.
      try {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        done();
      } catch {
        toast.error('Copy failed — select the text and press Ctrl+C');
      }
    }
  };

  const parseOptions = (text) => {
    if (!text) return [];
    const headers = [];
    let m;
    const headerFinder = /\*{0,2}\s*OPTION\s+([A-C])\s*[:\s–—-]*\s*(.*?)(?:\*{0,2})\s*(?:\n|$)/gi;
    while ((m = headerFinder.exec(text)) !== null) {
      headers.push({
        label: `Option ${m[1].toUpperCase()}`,
        subtitle: m[2]?.replace(/\*+/g, '').replace(/^[:\s–—-]+|[:\s–—-]+$/g, '').trim() || '',
        start: m.index,
        headerEnd: m.index + m[0].length,
      });
    }
    if (headers.length === 0) {
      return [{ label: 'Generated Caption', subtitle: '', content: text.trim() }];
    }
    return headers.map((h, i) => {
      const contentStart = h.headerEnd;
      const contentEnd = i < headers.length - 1 ? headers[i + 1].start : text.length;
      const content = text.slice(contentStart, contentEnd).trim();
      return { label: h.label, subtitle: h.subtitle, content };
    });
  };

  return (
    <div className="space-y-8 max-w-4xl" data-testid="caption-generator-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold text-zinc-900 tracking-tight">Caption Generator</h1>
        <p className="text-sm text-zinc-500 mt-1">
          Upload a Reel or post and AI watches it, then writes virality- &amp; SEO-optimized captions for Instagram &amp; TikTok.
        </p>
      </div>

      {/* Input Section */}
      <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
        <div className="p-6 space-y-5">
          {/* Media upload */}
          <div>
            <label className="text-sm font-medium text-zinc-700 mb-2 block">
              Upload the post <span className="text-zinc-400 font-normal">(optional — video or image; AI reads it)</span>
            </label>
            <input
              ref={fileRef}
              type="file"
              accept="video/*,image/*"
              className="hidden"
              onChange={(e) => pickFile(e.target.files?.[0])}
              data-testid="caption-media-input"
            />
            {previewUrl ? (
              <div className="relative rounded-md border border-zinc-200 overflow-hidden bg-zinc-900 max-h-72 flex items-center justify-center" data-testid="caption-media-preview">
                {mediaKind === 'video' ? (
                  <video src={previewUrl} controls className="max-h-72 w-auto" />
                ) : (
                  <img src={previewUrl} alt="preview" className="max-h-72 w-auto object-contain" />
                )}
                <button
                  onClick={clearMedia}
                  className="absolute top-2 right-2 bg-white/90 hover:bg-white p-1.5 rounded-full shadow-sm"
                  title="Remove"
                  data-testid="caption-media-remove"
                >
                  <X size={14} />
                </button>
                <span className="absolute top-2 left-2 bg-black/70 text-white text-[10px] uppercase tracking-wider px-2 py-0.5 rounded inline-flex items-center gap-1">
                  {mediaKind === 'video' ? <Film size={10} /> : <ImageIcon size={10} />} {mediaKind}
                </span>
              </div>
            ) : (
              <button
                onClick={() => fileRef.current?.click()}
                className="w-full border-2 border-dashed border-zinc-200 rounded-md py-6 hover:border-zinc-300 hover:bg-zinc-50 transition-colors flex flex-col items-center justify-center gap-1.5"
                data-testid="caption-media-zone"
              >
                <Upload size={20} className="text-zinc-400" />
                <span className="text-sm text-zinc-600 font-medium">Click to upload a video or image</span>
                <span className="text-[11px] text-zinc-400">MP4 / MOV / PNG / JPG — any length</span>
              </button>
            )}
          </div>

          {/* Text context */}
          <div>
            <label className="text-sm font-medium text-zinc-700 mb-2 block">
              Notes / concept <span className="text-zinc-400 font-normal">(optional if you uploaded a post)</span>
            </label>
            <Textarea
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              placeholder="Anything the AI should know… e.g. 'Custom oval solitaire reveal, customer teared up, want it to feel emotional and drive DMs'"
              rows={4}
              className="resize-none"
              data-testid="caption-input"
            />
          </div>

          {/* Controls */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-xs text-zinc-500">Post:</span>
                <div className="flex gap-1">
                  {POST_TYPES.map((pt) => (
                    <button
                      key={pt.value}
                      onClick={() => setPostType(pt.value)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                        postType === pt.value ? 'bg-zinc-900 text-white' : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
                      }`}
                      data-testid={`post-type-${pt.value}`}
                    >
                      {pt.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-zinc-500">Platform:</span>
                <div className="flex gap-1">
                  {PLATFORMS.map((pl) => (
                    <button
                      key={pl.value}
                      onClick={() => setPlatform(pl.value)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                        platform === pl.value ? 'bg-violet-600 text-white' : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
                      }`}
                      data-testid={`platform-${pl.value}`}
                    >
                      {pl.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <Button
              onClick={handleGenerate}
              disabled={generating || (!inputText.trim() && !file)}
              className="bg-zinc-900 hover:bg-zinc-800 text-white"
              data-testid="generate-captions-btn"
            >
              {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              {generating ? 'Generating...' : 'Generate Captions'}
            </Button>
          </div>
        </div>
      </div>

      {/* Progress */}
      {generating && (
        <div className="bg-white border border-zinc-200 rounded-sm p-10 text-center" data-testid="caption-progress">
          <Loader2 className="w-8 h-8 animate-spin text-zinc-400 mx-auto mb-3" />
          <p className="text-sm font-medium text-zinc-700">{progressMsg || 'Working…'}</p>
          <p className="text-xs text-zinc-400 mt-1">
            {file ? 'Reading your post and writing for maximum reach…' : 'Analyzing content for maximum reach and engagement'}
          </p>
        </div>
      )}

      {/* Result */}
      {result && !generating && (
        <div className="space-y-4" data-testid="caption-results">
          {result.media_analysis && (
            <div className="bg-violet-50 border border-violet-100 rounded-sm overflow-hidden">
              <button
                onClick={() => setShowAnalysis((v) => !v)}
                className="w-full px-5 py-3 flex items-center justify-between text-left"
                data-testid="caption-analysis-toggle"
              >
                <span className="text-sm font-semibold text-violet-800 inline-flex items-center gap-2">
                  <Eye className="w-4 h-4" /> What the AI saw in your {result.media_kind || 'post'}
                </span>
                {showAnalysis ? <ChevronUp className="w-4 h-4 text-violet-500" /> : <ChevronDown className="w-4 h-4 text-violet-500" />}
              </button>
              {showAnalysis && (
                <div className="px-5 pb-4 -mt-1">
                  <pre className="text-[13px] text-violet-900/80 whitespace-pre-wrap font-sans leading-relaxed" data-testid="caption-ai-analysis">
                    {result.media_analysis}
                  </pre>
                </div>
              )}
            </div>
          )}

          <h2 className="text-sm font-semibold text-zinc-700 uppercase tracking-widest flex items-center gap-2">
            <Type className="w-4 h-4" /> Generated Captions
          </h2>
          {parseOptions(result.generated_captions).map((opt, i) => (
            <div key={i} className="bg-white border border-zinc-200 rounded-sm overflow-hidden" data-testid={`caption-option-${i}`}>
              <div className="px-5 py-3 bg-zinc-50 border-b border-zinc-100 flex items-center justify-between">
                <div>
                  <span className="text-sm font-semibold text-zinc-800">{opt.label}</span>
                  {opt.subtitle && <span className="text-xs text-zinc-500 ml-2">{opt.subtitle}</span>}
                </div>
                <Button
                  size="sm" variant="ghost"
                  onClick={() => copyCaption(opt.content, `result-${i}`)}
                  className="text-zinc-500 hover:text-zinc-900"
                  data-testid={`copy-caption-${i}`}
                >
                  {copiedId === `result-${i}` ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
              <div className="p-5">
                <pre className="text-sm text-zinc-700 whitespace-pre-wrap font-sans leading-relaxed">{opt.content}</pre>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* History */}
      {history.length > 0 && (
        <div className="space-y-3" data-testid="caption-history">
          <h2 className="text-sm font-semibold text-zinc-700 uppercase tracking-widest flex items-center gap-2">
            <Clock className="w-4 h-4" /> Previous Generations
          </h2>
          {history.map((item) => {
            const isExpanded = expandedHistory === item.id;
            const isCurrentResult = result?.id === item.id;
            if (isCurrentResult) return null;
            return (
              <div key={item.id} className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
                <div
                  className="px-5 py-3 flex items-center justify-between cursor-pointer hover:bg-zinc-50 transition-colors"
                  onClick={() => setExpandedHistory(isExpanded ? null : item.id)}
                >
                  <div className="flex-1 min-w-0 mr-4">
                    <p className="text-sm text-zinc-800 truncate">
                      {item.input_text || (item.media_kind ? `(${item.media_kind} post)` : '—')}
                    </p>
                    <div className="flex items-center gap-3 mt-1 text-xs text-zinc-400">
                      {item.media_kind && (
                        <span className="px-1.5 py-0.5 bg-violet-50 text-violet-600 rounded inline-flex items-center gap-1">
                          {item.media_kind === 'video' ? <Film size={10} /> : <ImageIcon size={10} />} {item.media_kind}
                        </span>
                      )}
                      <span className="px-1.5 py-0.5 bg-zinc-100 rounded text-zinc-500">{item.post_type}</span>
                      {item.status === 'processing' && (
                        <span className="text-amber-600 inline-flex items-center gap-1"><Loader2 size={10} className="animate-spin" /> processing</span>
                      )}
                      {item.status === 'error' && <span className="text-red-500">failed</span>}
                      <span>{new Date(item.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      size="sm" variant="ghost"
                      className="text-zinc-400 hover:text-zinc-700"
                      onClick={(e) => { e.stopPropagation(); copyCaption(item.generated_captions, item.id); }}
                    >
                      {copiedId === item.id ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
                    </Button>
                    <Button
                      size="sm" variant="ghost"
                      className="text-zinc-400 hover:text-red-500"
                      onClick={(e) => { e.stopPropagation(); handleDelete(item.id); }}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                    {isExpanded ? <ChevronUp className="w-4 h-4 text-zinc-400" /> : <ChevronDown className="w-4 h-4 text-zinc-400" />}
                  </div>
                </div>
                {isExpanded && (
                  <div className="border-t border-zinc-100 p-5">
                    {item.media_analysis && (
                      <div className="mb-4 text-[12px] text-violet-900/70 bg-violet-50/50 rounded p-3">
                        <span className="font-semibold inline-flex items-center gap-1"><Eye size={12} /> AI saw:</span>
                        <pre className="whitespace-pre-wrap font-sans mt-1">{item.media_analysis}</pre>
                      </div>
                    )}
                    {parseOptions(item.generated_captions).map((opt, i) => (
                      <div key={i} className="mb-4 last:mb-0">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-semibold text-zinc-600 uppercase">{opt.label} {opt.subtitle && `— ${opt.subtitle}`}</span>
                          <button
                            onClick={() => copyCaption(opt.content, `${item.id}-${i}`)}
                            className="text-zinc-400 hover:text-zinc-700"
                          >
                            {copiedId === `${item.id}-${i}` ? <Check size={12} className="text-green-600" /> : <Copy size={12} />}
                          </button>
                        </div>
                        <pre className="text-sm text-zinc-600 whitespace-pre-wrap font-sans leading-relaxed bg-zinc-50 p-3 rounded">{opt.content}</pre>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CaptionGenerator;
